import { useConfigStore } from '@/store/useConfigStore';
import { InputField } from '@/components/ui/InputField';
import { StepperInput } from '@/components/ui/StepperInput';
import { cn } from '@/lib/utils';
import { Circle, Hexagon, Flower2, Grip, AlignCenterVertical } from 'lucide-react';

export function Step3FabricLining() {
  const { fabricConfig, liningConfig, hardwareConfig, updateFabricConfig, updateLiningConfig, updateHardwareConfig } = useConfigStore();

  const opacityLevels = [
    { label: 'Sheer', value: 'sheer' },
    { label: 'Light Filtering', value: 'light-filtering' },
    { label: 'Room Darkening', value: 'room-darkening' },
    { label: 'Blackout', value: 'blackout' },
  ];

  const fabrics = [
    { label: 'Linen', value: 'linen' },
    { label: 'Velvet', value: 'velvet' },
    { label: 'Cotton', value: 'cotton' },
    { label: 'Silk', value: 'silk' },
    { label: 'Jacquard', value: 'jacquard' },
    { label: 'Polyester', value: 'polyester' },
  ];

  const patterns = [
    { name: 'Solid', value: 'solid', icon: Circle },
    { name: 'Geometric', value: 'geometric', icon: Hexagon },
    { name: 'Floral', value: 'floral', icon: Flower2 },
    { name: 'Striped', value: 'striped', icon: AlignCenterVertical },
    { name: 'Herringbone', value: 'herringbone', icon: Grip },
  ];
  
  const liningTypes = [
    { label: 'None', value: 'none' },
    { label: 'Standard', value: 'standard' },
    { label: 'Thermal', value: 'thermal' },
    { label: 'Blackout', value: 'blackout' },
  ];

  const hardwareStyles = [
    { label: 'Architectural', value: 'architectural' },
    { label: 'Decorative', value: 'decorative' },
  ];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Fabric & Lining</h2>
          <p className="text-slate-600">Select materials, patterns, and lining options.</p>
        </div>
        <button
          onClick={() => useConfigStore.getState().setVisualizerOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-medium transition-colors border border-indigo-200"
        >
          <span>👓 View in 3D</span>
        </button>
      </div>

      {/* Fabric Type */}
      <div className="space-y-4">
        <label className="text-sm font-medium text-slate-700 block">Fabric Type</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {fabrics.map((fabric) => (
            <button
              key={fabric.value}
              onClick={() => updateFabricConfig({ fabricType: fabric.value as any })}
              className={cn(
                "p-4 rounded-xl border-2 text-center font-medium transition-all",
                fabricConfig.fabricType === fabric.value
                  ? "border-amber-500 bg-amber-50/50 text-slate-900 shadow-sm"
                  : "border-slate-200 bg-white hover:border-slate-300 text-slate-700"
              )}
            >
              {fabric.label}
            </button>
          ))}
        </div>
      </div>

      {/* Opacity */}
      <div className="space-y-4 pt-4 border-t border-slate-100">
        <label className="text-sm font-medium text-slate-700 block">Opacity</label>
        <div className="flex bg-slate-100 p-1 rounded-xl overflow-x-auto snap-x">
          {opacityLevels.map((opacity) => (
            <button
              key={opacity.value}
              onClick={() => updateFabricConfig({ opacity: opacity.value as any })}
              className={cn(
                "flex-1 min-w-[120px] py-2.5 px-4 rounded-lg text-sm font-medium transition-all snap-center whitespace-nowrap",
                fabricConfig.opacity === opacity.value
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/50"
              )}
            >
              {opacity.label}
            </button>
          ))}
        </div>
      </div>

      {/* Pattern */}
      <div className="space-y-4 pt-4 border-t border-slate-100">
        <label className="text-sm font-medium text-slate-700 block">Pattern</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {patterns.map((pattern) => {
            const Icon = pattern.icon;
            const isSelected = fabricConfig.pattern === pattern.value;
            return (
              <button
                key={pattern.value}
                onClick={() => updateFabricConfig({ pattern: pattern.value as any })}
                className={cn(
                  "flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all gap-2",
                  isSelected
                    ? "border-amber-500 bg-amber-50/50 text-slate-900 shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300 text-slate-600"
                )}
              >
                <Icon size={24} className={isSelected ? "text-amber-500" : "text-slate-400"} />
                <span className="text-sm font-medium">{pattern.name}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Measurements & Price */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-100">
        <StepperInput
          label="Fabric Width"
          value={fabricConfig.fabricWidth || 1400}
          onChange={(val) => updateFabricConfig({ fabricWidth: val })}
          unit="mm"
          min={1000}
          max={3000}
        />
        <StepperInput
          label="Vertical Repeat"
          value={fabricConfig.verticalRepeat || 0}
          onChange={(val) => updateFabricConfig({ verticalRepeat: val })}
          unit="mm"
          min={0}
          max={2000}
        />
        <InputField
          label="Price per Meter"
          type="number"
          min="0"
          step="0.01"
          value={fabricConfig.pricePerMeter || ''}
          onChange={(e) => updateFabricConfig({ pricePerMeter: parseFloat(e.target.value) || 0 })}
          unit="$"
          placeholder="e.g. 45.00"
        />
      </div>

      {/* Lining & Hardware */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-slate-100">
        <div className="space-y-4">
          <label className="text-sm font-medium text-slate-700 block">Lining Type</label>
          <div className="grid grid-cols-2 gap-3">
            {liningTypes.map((lining) => (
              <button
                key={lining.value}
                onClick={() => updateLiningConfig({ liningType: lining.value as any })}
                className={cn(
                  "p-3 rounded-lg border-2 text-center text-sm font-medium transition-all",
                  liningConfig.liningType === lining.value
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white hover:border-slate-300 text-slate-700"
                )}
              >
                {lining.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <label className="text-sm font-medium text-slate-700 block">Hardware Style</label>
          <div className="grid grid-cols-2 gap-3">
            {hardwareStyles.map((hw) => (
              <button
                key={hw.value}
                onClick={() => updateHardwareConfig({ style: hw.value as any })}
                className={cn(
                  "p-3 rounded-lg border-2 text-center text-sm font-medium transition-all",
                  hardwareConfig.style === hw.value
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white hover:border-slate-300 text-slate-700"
                )}
              >
                {hw.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
